import type { PublicQuote } from "../types";
import { formatIsoDate, formatMxn } from "./quoterFormat";

type Translate = (key: string, options?: Record<string, unknown>) => string;

const SQUARE_METERS = (value: number) =>
  value.toLocaleString("es-MX", { maximumFractionDigits: 2 });

/** The digits of an E.164 number (`+52...`), the form `wa.me` expects. */
export const whatsappDigits = (e164: string): string => e164.replace(/\D/g, "");

/** `https://wa.me/<digits>` with an optional prefilled text. */
export const whatsappUrl = (e164: string | null, text?: string): string => {
  const base = `https://wa.me/${e164 ? whatsappDigits(e164) : ""}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
};

/** First word of the advisor's name, for the greeting in the QR message. */
const firstName = (fullName: string) => fullName.trim().split(/\s+/)[0] ?? "";

/** The text the advisor-card QR opens the chat with. */
export const buildQrMessage = (
  quote: PublicQuote,
  translate: Translate,
): string =>
  translate("crm.quoter.public.wa.qr_message", {
    advisor: firstName(quote.detail?.advisor.name ?? ""),
    folio: quote.detail?.folio ?? "",
  });

/**
 * The prefilled WhatsApp summary of the quote (the same lines as the owner's
 * reference app, with the Almikia name): greeting, unit, size, price and plan,
 * validity and the advisor's signature. Sent without a number so the client
 * picks the chat (the page never knows the client's phone).
 */
export const buildSummaryMessage = (
  quote: PublicQuote,
  translate: Translate,
): string => {
  const detail = quote.detail;
  if (!detail) return "";
  const { unit, prices, advisor } = detail;
  const sizeParts = [
    unit.total_construction != null
      ? translate("crm.quoter.public.wa.construction", {
          m2: SQUARE_METERS(unit.total_construction),
        })
      : null,
    unit.surface_m2 != null
      ? translate("crm.quoter.public.wa.surface", {
          m2: SQUARE_METERS(unit.surface_m2),
        })
      : null,
  ].filter((part): part is string => part !== null);
  const lines = [
    translate("crm.quoter.public.wa.intro", {
      name: quote.client_first_name,
      folio: detail.folio,
    }),
    "",
    translate(
      unit.lot_type
        ? "crm.quoter.public.wa.unit_typed"
        : "crm.quoter.public.wa.unit",
      {
        model: unit.model_name ?? "",
        unit: quote.unit_identifier,
        lotType: unit.lot_type ?? "",
      },
    ),
    ...(sizeParts.length > 0 ? [`• ${sizeParts.join(" · ")}`] : []),
    ...(prices.roof > 0 ? [translate("crm.quoter.public.wa.roof")] : []),
    translate("crm.quoter.public.wa.price", {
      price: formatMxn(prices.final),
      scheme: detail.scheme_name,
    }),
    translate("crm.quoter.public.wa.valid", {
      date: formatIsoDate(detail.valid_until),
    }),
    "",
    translate("crm.quoter.public.wa.closing"),
    advisor.whatsapp ? `${advisor.name} · ${advisor.whatsapp}` : advisor.name,
  ];
  return lines.join("\n");
};
